import AddIcon from "@mui/icons-material/Add";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import React from "react";
import { useSelector } from "react-redux";
import { useTranslationHook } from "../../hooks";
import { StoreInterfaces } from "../../types/store";

export type StatCardProps = {
  title: string;
  total?: number | string;
  active?: number;
  closed?: number;
  ending?: number;
  icon?: React.ReactElement;
  onAdd?: () => void;
  onView?: () => void;
  isActive?: boolean;
  onClick?: () => void;
  accentColor?: string;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(2),
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  background: theme.vars
    ? `linear-gradient(180deg, rgba(${theme.vars.palette.background.paperChannel} / 0.6) 0%, rgba(${theme.vars.palette.background.defaultChannel} / 0.8) 100%)`
    : alpha(theme.palette.background.paper, 0.7),
  boxShadow: theme.shadows[1],
  transition: "all 0.25s ease",
  "&:hover": {
    boxShadow: theme.shadows[6],
    transform: "translateY(-3px)",
  },
}));

export default function StatCard({
  title,
  total,
  active,
  closed,
  ending,
  icon,
  onAdd,
  onView,
  isActive,
  onClick,
  accentColor,
}: Readonly<StatCardProps>) {
  const { t } = useTranslationHook();
  const session = useSelector((state: StoreInterfaces) => state.session);
  const isManager = session?.isManager || false;

  const primaryValue = total ?? active ?? ending ?? closed;

  const showActiveChip = total !== undefined && active !== undefined;

  const secondaryStats: { label: string; value: number }[] =
    total === undefined
      ? []
      : [
          closed !== undefined && { label: t("generic.stats.closed"), value: closed },
          ending !== undefined && { label: t("generic.stats.ending"), value: ending },
        ].filter((s): s is { label: string; value: number } => Boolean(s));

  return (
    <StyledCard
      variant="outlined"
      onClick={onClick}
      sx={{
        borderTop: accentColor ? `3px solid ${accentColor}` : undefined,
        ...(isActive
          ? { borderColor: "secondary.main", borderWidth: 2 }
          : {}),
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flexGrow: 1,
          "&:last-child": { pb: 2.5 },
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            {icon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: (theme) =>
                    alpha(accentColor ?? theme.palette.primary.main, 0.12),
                  color: accentColor ?? "primary.main",
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(icon, { sx: { fontSize: 20 } })}
              </Box>
            )}
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          </Stack>

          {(onAdd || onView) && isManager && (
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {onView && (
                <Tooltip title={t("generic.view-all")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onView();
                    }}
                    sx={(theme) => ({
                      bgcolor: theme.palette.action.selected,
                      color: theme.palette.text.primary,
                      "&:hover": { bgcolor: theme.palette.action.hover, transform: "scale(1.08)" },
                      transition: "0.2s",
                    })}
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {onAdd && (
                <Tooltip title={t("generic.create")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdd();
                    }}
                    sx={(theme) => ({
                      bgcolor: accentColor ?? theme.palette.secondary.main,
                      color: theme.palette.getContrastText(accentColor ?? theme.palette.secondary.main),
                      "&:hover": {
                        bgcolor: accentColor ? alpha(accentColor, 0.85) : theme.palette.secondary.dark,
                        transform: "scale(1.08)",
                      },
                      transition: "0.2s",
                    })}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: "auto" }}>
          <Typography variant="h3" component="p" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {primaryValue}
          </Typography>

          {showActiveChip && (
            <Chip
              size="small"
              label={`${active} ${t("generic.stats.active")}`}
              sx={(theme) => ({
                fontWeight: 600,
                bgcolor: alpha(theme.palette.success.main, 0.14),
                color: theme.palette.mode === "light"
                  ? theme.palette.success.dark
                  : theme.palette.success.light,
              })}
            />
          )}
        </Stack>

        {secondaryStats.length > 0 && (
          <Stack direction="row" spacing={2.5} sx={{ pt: 0.5 }}>
            {secondaryStats.map((stat) => (
              <Stack key={stat.label} direction="row" spacing={0.5} alignItems="baseline">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {stat.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </StyledCard>
  );
}